/**
 * AuthContext.js -- Central authentication provider for the entire app.
 *
 * Auth flow on mount (checkAuth):
 *   1. Fetch /api/v1/auth/status from the backend to discover the auth mode.
 *   2. If Cognito is configured (env vars present), check for an existing Cognito session.
 *      - If authenticated, get the Cognito ID token and store it via setToken().
 *      - Attempt to fetch the full user profile from /api/v1/auth/me.
 *      - If the backend is unreachable, fall back to decoding basic user info from the JWT payload.
 *   3. If Cognito is NOT configured, check for a stored JWT token.
 *      - Verify it by calling /api/v1/auth/me.
 *      - If invalid (401), clear the token.
 *      - If auth_mode === 'dev', auto-login via devLogin().
 *
 * Three auth modes:
 *   - Cognito:   AWS Cognito User Pool (sign-in, sign-up, MFA challenges, verification codes).
 *   - JWT:       Backend-issued JWT via /api/v1/auth/login (username + password).
 *   - dev-token: Auto-login as admin via /api/v1/auth/token/dev (no credentials needed).
 *
 * Why fetch() instead of Axios:
 *   The Axios instance in apiClient.js has a request interceptor that reads the auth token
 *   from this context. Using Axios here would create a circular dependency
 *   (AuthContext -> apiClient -> AuthContext). Raw fetch() avoids this loop.
 *
 * Token refresh:
 *   - Cognito mode: cognitoGetIdToken() returns a fresh token from the Amplify session
 *     (Amplify handles refresh transparently).
 *   - JWT mode: the refresh_token (if returned by the backend) is stored via setRefreshToken();
 *     actual refresh is handled by the Axios interceptor in apiClient.js.
 *   - refreshUser() re-fetches the user profile and, for Cognito, refreshes the stored token.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getToken, setToken, clearToken, setRefreshToken } from '../services/apiClient';
// Import Amplify configuration (initializes Cognito if configured)
import '../config/amplify';
import {
  isCognitoConfigured,
  cognitoSignIn,
  cognitoSignOut,
  cognitoSignUp,
  cognitoConfirmSignUp,
  cognitoResendSignUpCode,
  cognitoGetIdToken,
  cognitoIsAuthenticated,
  cognitoForgotPassword,
  cognitoConfirmForgotPassword
} from '../services/cognitoAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState(null);

  // Check auth status on mount -- runs once. Determines auth mode and restores session.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Step 1: Discover auth mode from the backend (Cognito / JWT / dev).
        // Uses raw fetch() with a 5s timeout to avoid blocking the app if backend is down.
        let status = null;
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          const statusRes = await fetch(`${API_URL}/api/v1/auth/status`, {
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          status = await statusRes.json();
          setAuthStatus(status);
        } catch (e) {
          console.warn('Could not fetch auth status from backend:', e.message);
          // If backend is unreachable, fall through to Cognito-only path below
        }

        // Step 2: Try Cognito session restoration (takes priority if configured)
        if (isCognitoConfigured()) {
          const isAuthenticated = await cognitoIsAuthenticated();
          if (isAuthenticated) {
            const idToken = await cognitoGetIdToken();
            if (idToken) {
              setToken(idToken); // Store Cognito token for API calls

              // Try to fetch user profile from backend (but don't fail if unavailable)
              try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);
                const res = await fetch(`${API_URL}/api/v1/auth/me`, {
                  headers: { Authorization: `Bearer ${idToken}` },
                  signal: controller.signal
                });
                clearTimeout(timeoutId);
                if (res.ok) {
                  const userData = await res.json();
                  setUser(userData);
                } else {
                  // Backend returned error - use basic user info from token
                  try {
                    const tokenPayload = JSON.parse(atob(idToken.split('.')[1]));
                    setUser({
                      user_id: tokenPayload.sub,
                      username: tokenPayload.email?.split('@')[0] || 'User',
                      email: tokenPayload.email,
                      role: 'manager',
                      groups: [],
                      is_active: true
                    });
                  } catch (decodeErr) {
                    console.error('Failed to decode token:', decodeErr);
                    logout();
                  }
                }
              } catch (e) {
                console.warn('Could not fetch user profile from backend:', e.message);
                // Use basic user info from Cognito token
                try {
                  const tokenPayload = JSON.parse(atob(idToken.split('.')[1]));
                  setUser({
                    user_id: tokenPayload.sub,
                    username: tokenPayload.email?.split('@')[0] || 'User',
                    email: tokenPayload.email,
                    role: 'manager',
                    groups: [],
                    is_active: true
                  });
                } catch (decodeErr) {
                  console.error('Failed to decode token:', decodeErr);
                  logout();
                }
              }
            }
          }
        } else if (status) {
          // Step 3: No Cognito -- fall back to JWT/dev-token mode. Check stored token.
          const token = getToken();
          if (token) {
            // Verify the token
            const res = await fetch(`${API_URL}/api/v1/auth/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (res.ok) {
              const userData = await res.json();
              setUser(userData);
            } else {
              // Token invalid, clear it
              clearToken();

              // In dev mode, auto-login
              if (status.auth_mode === 'dev') {
                await devLogin();
              }
            }
          } else if (status.auth_mode === 'dev') {
            // Dev mode - auto login
            await devLogin();
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // devLogin -- Auto-login for local development (auth_mode === 'dev').
  // Requests a token from the /token/dev endpoint with hardcoded dev credentials.
  const devLogin = async () => {
    try {
      const tokenRes = await fetch(`${API_URL}/api/v1/auth/token/dev`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: 'dev-user',
          email: 'dev@localhost',
          role: 'admin',
          groups: ['eng-dept']
        })
      });

      if (tokenRes.ok) {
        const data = await tokenRes.json();
        setToken(data.access_token);
        if (data.refresh_token) {
          setRefreshToken(data.refresh_token);
        }

        // Fetch user profile
        const profileRes = await fetch(`${API_URL}/api/v1/auth/me`, {
          headers: { Authorization: `Bearer ${data.access_token}` }
        });
        if (profileRes.ok) {
          setUser(await profileRes.json());
        }
      }
    } catch (error) {
      console.error('Dev login failed:', error);
    }
  };

  // login -- Dispatches to Cognito or JWT based on configuration.
  // Cognito path handles challenges (e.g. NEW_PASSWORD_REQUIRED) and maps errors to friendly messages.
  // JWT path POSTs to /api/v1/auth/login and stores both access and refresh tokens.
  const login = useCallback(async (username, password) => {
    // Use Cognito if configured
    if (isCognitoConfigured()) {
      try {
        const result = await cognitoSignIn(username, password);

        // Handle sign-in challenges (e.g., NEW_PASSWORD_REQUIRED)
        if (result.challengeName) {
          return {
            challenge: result.challengeName,
            session: result.session
          };
        }

        // Get ID token for backend API calls
        const idToken = await cognitoGetIdToken();
        if (!idToken) {
          throw new Error('Failed to get authentication token');
        }

        setToken(idToken); // Store for API calls

        // Try to fetch user profile from backend (with timeout, don't fail if unavailable)
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
          const profileRes = await fetch(`${API_URL}/api/v1/auth/me`, {
            headers: { Authorization: `Bearer ${idToken}` },
            signal: controller.signal
          });
          clearTimeout(timeoutId);

          if (profileRes.ok) {
            const userData = await profileRes.json();
            setUser(userData);
            return userData;
          }
        } catch (e) {
          console.warn('Could not fetch user profile from backend:', e.message);
        }

        // Fallback: use basic user info from Cognito token
        const tokenPayload = JSON.parse(atob(idToken.split('.')[1]));
        const userData = {
          user_id: tokenPayload.sub,
          username: tokenPayload.email?.split('@')[0] || username,
          email: tokenPayload.email || username,
          role: 'manager',
          groups: [],
          is_active: true
        };
        setUser(userData);
        return userData;
      } catch (error) {
        // Map Cognito errors to user-friendly messages
        if (error.name === 'NotAuthorizedException') {
          throw new Error('Invalid email or password');
        } else if (error.name === 'UserNotConfirmedException') {
          throw new Error('Please verify your email before signing in');
        } else if (error.name === 'UserNotFoundException') {
          throw new Error('Invalid email or password');
        }
        throw error;
      }
    }

    // Fallback to JWT login
    const res = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.detail || 'Login failed');
    }

    setToken(data.access_token);
    if (data.refresh_token) {
      setRefreshToken(data.refresh_token);
    }

    // Fetch user profile
    const profileRes = await fetch(`${API_URL}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${data.access_token}` }
    });

    if (profileRes.ok) {
      const userData = await profileRes.json();
      setUser(userData);
      return userData;
    }

    throw new Error('Failed to fetch user profile');
  }, []);

  const register = useCallback(async (username, email, password) => {
    // Use Cognito if configured
    if (isCognitoConfigured()) {
      try {
        const result = await cognitoSignUp(email, password, username);
        return {
          success: true,
          requiresConfirmation: !result.isConfirmed,
          email: email,
          message: result.isConfirmed
            ? 'Registration successful! You can now sign in.'
            : 'Please check your email for a verification code.'
        };
      } catch (error) {
        if (error.name === 'UsernameExistsException') {
          throw new Error('An account with this email already exists');
        } else if (error.name === 'InvalidPasswordException') {
          throw new Error('Password does not meet requirements');
        }
        throw error;
      }
    }

    // Fallback to backend registration
    const res = await fetch(`${API_URL}/api/v1/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password })
    });

    const data = await res.json();

    if (!res.ok) {
      if (data.detail?.message) {
        throw new Error(data.detail.message);
      }
      throw new Error(data.detail || 'Registration failed');
    }

    return data;
  }, []);

  // Confirm sign up with verification code (Cognito only)
  const confirmSignUp = useCallback(async (email, code) => {
    if (!isCognitoConfigured()) {
      throw new Error('Email verification is handled via email link');
    }

    try {
      const result = await cognitoConfirmSignUp(email, code);
      return {
        success: result.success,
        message: 'Email verified successfully! You can now sign in.'
      };
    } catch (error) {
      if (error.name === 'CodeMismatchException') {
        throw new Error('Invalid verification code');
      } else if (error.name === 'ExpiredCodeException') {
        throw new Error('Verification code has expired. Please request a new one.');
      }
      throw error;
    }
  }, []);

  // Resend sign up verification code (Cognito only)
  const resendSignUpCode = useCallback(async (email) => {
    if (!isCognitoConfigured()) {
      throw new Error('Email verification is handled via email link');
    }

    try {
      await cognitoResendSignUpCode(email);
      return {
        success: true,
        message: 'Verification code sent to your email.'
      };
    } catch (error) {
      if (error.name === 'LimitExceededException') {
        throw new Error('Too many attempts. Please try again later.');
      }
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Sign out from Cognito if configured
      if (isCognitoConfigured()) {
        await cognitoSignOut();
      } else {
        // Backend logout for JWT mode
        const token = getToken();
        if (token) {
          await fetch(`${API_URL}/api/v1/auth/logout`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearToken();
      setUser(null);
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    // Use Cognito if configured
    if (isCognitoConfigured()) {
      try {
        await cognitoForgotPassword(email);
        return {
          success: true,
          message: 'Password reset code sent to your email'
        };
      } catch (error) {
        if (error.name === 'UserNotFoundException') {
          throw new Error('No account found with this email');
        } else if (error.name === 'LimitExceededException') {
          throw new Error('Too many attempts. Please try again later.');
        }
        throw error;
      }
    }

    // Fallback to backend
    const res = await fetch(`${API_URL}/api/v1/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.detail || 'Failed to send reset email');
    }

    return data;
  }, []);

  // For Cognito: (email, code, newPassword), For JWT: (token, newPassword)
  const resetPassword = useCallback(async (tokenOrEmail, newPasswordOrCode, newPasswordForCognito) => {
    // Use Cognito if configured (expects email, code, newPassword)
    if (isCognitoConfigured()) {
      const email = tokenOrEmail;
      const code = newPasswordOrCode;
      const newPassword = newPasswordForCognito;

      try {
        await cognitoConfirmForgotPassword(email, code, newPassword);
        return {
          success: true,
          message: 'Password reset successfully! You can now sign in.'
        };
      } catch (error) {
        if (error.name === 'CodeMismatchException') {
          throw new Error('Invalid verification code');
        } else if (error.name === 'ExpiredCodeException') {
          throw new Error('Verification code has expired. Please request a new one.');
        } else if (error.name === 'InvalidPasswordException') {
          throw new Error('Password does not meet requirements');
        }
        throw error;
      }
    }

    // Fallback to backend (expects token, newPassword)
    const token = tokenOrEmail;
    const newPassword = newPasswordOrCode;

    const res = await fetch(`${API_URL}/api/v1/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword })
    });

    const data = await res.json();

    if (!res.ok) {
      if (data.detail?.message) {
        throw new Error(data.detail.message);
      }
      throw new Error(data.detail || 'Password reset failed');
    }

    return data;
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const res = await fetch(`${API_URL}/api/v1/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.detail || 'Email verification failed');
    }

    return data;
  }, []);

  const resendVerification = useCallback(async (email) => {
    const res = await fetch(`${API_URL}/api/v1/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.detail || 'Failed to resend verification');
    }

    return data;
  }, []);

  // refreshUser -- Re-fetches user profile. For Cognito, also refreshes the stored ID token
  // (Amplify handles the actual token refresh via its session management).
  const refreshUser = useCallback(async () => {
    try {
      let token;

      // For Cognito, refresh token from session
      if (isCognitoConfigured()) {
        token = await cognitoGetIdToken();
        if (token) {
          setToken(token); // Update stored token
        }
      } else {
        token = getToken();
      }

      if (token) {
        const res = await fetch(`${API_URL}/api/v1/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
          return userData;
        }
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
    return null;
  }, []);

  const value = {
    user,
    loading,
    authStatus,
    isAuthenticated: !!user,
    isDevMode: authStatus?.auth_mode === 'dev',
    isCognitoMode: isCognitoConfigured(),
    login,
    register,
    confirmSignUp,
    resendSignUpCode,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
